import { useLingui } from "@lingui/react/macro";
import { Badge } from "@offstreetparking/design-system/dataDisplay/Badge";
import { LoadingSpinner } from "@offstreetparking/design-system/feedback";
import { formatDate, formatDateRange } from "@offstreetparking/design-system/formatters";
import { RadioCard } from "@offstreetparking/design-system/forms";
import { useLocale } from "@offstreetparking/design-system/providers";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { offstreetClient } from "../../../../api/client";
import type { RouterInputs, RouterOutputs } from "../../../../api/types";
import { OverrideNoticeAlert } from "../../../OverrideNoticeAlert/OverrideNoticeAlert";
import { CapacityFullBadge } from "../../../dataDisplay/CapacityFullBadge";
import type { WizardParkingChoice } from "../../PermitContextProvider/types";
import { isChoiceIneligible } from "../../isChoiceIneligible";
import { requiresUserDateTimeInput } from "../../requiresUserDateTimeInput";
import type { OverrideNotices } from "../../types";
import { LocationSelectionOverlay } from "./LocationSelectionOverlay";
import { TimeSelectionForm } from "./TimeSelectionForm";
import { ParkingChoiceTag } from "./components/ParkingChoiceTag";
import { ParkingOptionDetails } from "./components/ParkingOptionDetails";
import { hasNeverExpiresIntervals } from "./hasNeverExpiresIntervals";
import type { PermitTimeFormData } from "./permitTimeSchema";
type ParkingOptionsListProps = {
	parkingOptions: WizardParkingChoice[];
	timezone: string;
	onSelectOption: (optionId: number, locationId?: number) => void;
	onSelectTime: (
		intervals: {
			startDate: string;
			startTime: string;
			endDate?: string;
			endTime?: string;
		}[],
	) => void;
	"aria-label"?: string;
	"aria-labelledby"?: string;
	preSelectedLocationId?: number;
	isSingleOption?: boolean;
	overrideNotices?: OverrideNotices;
	onSelectOverridePermitType?: (permitTypeId: number) => void;
};
type GetParkingChoiceCapacityStatusInput = RouterInputs["locationLookup"]["getParkingChoiceCapacityStatus"];
type GetParkingChoiceCapacityStatusOutput = RouterOutputs["locationLookup"]["getParkingChoiceCapacityStatus"];
type ChoiceCapacityStatusSnapshot = Pick<
	GetParkingChoiceCapacityStatusOutput,
	"choiceCapacity" | "intervalSource" | "locations"
> & {
	cacheKey: string;
};
type ParkingChoiceRawInterval = NonNullable<GetParkingChoiceCapacityStatusInput["rawIntervals"]>[number];
type AriaPermitInterval = {
	startDateTimeISO: string;
	endDateTimeISO?: string | null;
};
const MIN_FULL_CAPACITY_VALIDATION_MS = 300;
export const mapPermitIntervals = (
	intervals: PermitTimeFormData["choices"][number]["permitIntervals"],
	timezone: string,
) =>
	intervals.map((interval) => ({
		startDateTimeISO: DateTime.fromFormat(`${interval.startDate} ${interval.startTime}`, "yyyy-MM-dd HH:mm:ss", {
			zone: timezone,
		})
			.toUTC()
			.toISO()!,
		endDateTimeISO:
			interval.endDate && interval.endTime
				? DateTime.fromFormat(`${interval.endDate} ${interval.endTime}`, "yyyy-MM-dd HH:mm:ss", {
						zone: timezone,
					})
						.toUTC()
						.toISO()!
				: undefined,
		schedules: interval.schedules,
	}));
const toRawIntervals = (
	intervals: ReturnType<typeof mapPermitIntervals>,
): NonNullable<GetParkingChoiceCapacityStatusInput["rawIntervals"]> =>
	intervals
		.filter((interval) => !!interval.endDateTimeISO)
		.map((interval) => ({
			startDateTimeISO: interval.startDateTimeISO,
			endDateTimeISO: interval.endDateTimeISO!,
			schedules: interval.schedules,
		}));
const getCapacityCacheKey = (rawIntervals: ParkingChoiceRawInterval[]) => JSON.stringify(rawIntervals);
const toNotFullCapacityFallback = (parkingOption: WizardParkingChoice): GetParkingChoiceCapacityStatusOutput => ({
	parkingChoiceId: parkingOption.permitTypeId,
	choiceCapacity: "not-full",
	intervalSource: "none",
	locations: parkingOption.locations.map((location) => ({
		id: location.id,
		capacity: "not-full",
	})),
});
export const ParkingOptionsList = ({
	parkingOptions,
	timezone,
	onSelectOption,
	onSelectTime,
	"aria-label": listAriaLabel,
	"aria-labelledby": listAriaLabelledBy,
	preSelectedLocationId,
	isSingleOption = false,
	overrideNotices,
	onSelectOverridePermitType,
}: ParkingOptionsListProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	const trpcUtils = offstreetClient.useUtils();
	const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
	const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
	const [timeModalCapacityError, setTimeModalCapacityError] = useState<string | undefined>(undefined);
	const [validatingCapacityOptionId, setValidatingCapacityOptionId] = useState<number | null>(null);
	const [delayedFullOptionId, setDelayedFullOptionId] = useState<number | null>(null);
	const { watch, formState, setValue, clearErrors } = useFormContext<PermitTimeFormData>();
	const formChoices = watch("choices");
	const selectedOptionId = watch("selectedChoicesIds")?.[0];
	const selectedParkingOption = parkingOptions.find((option) => option.permitTypeId === selectedOptionId);
	const selectedFormChoice = formChoices.find((choice) => choice.parkingChoiceId === selectedOptionId);
	const selectedIntervals = mapPermitIntervals(selectedFormChoice?.permitIntervals ?? [], timezone);
	const hasSelectedIntervals =
		selectedIntervals.length > 0 && selectedIntervals.every((interval) => !!interval.endDateTimeISO);
	const selectedOptionRequiresUserDateTimeInput = selectedParkingOption
		? requiresUserDateTimeInput(selectedParkingOption)
		: false;
	const selectedOptionDefaultIntervals = selectedParkingOption
		? (selectedParkingOption.time?.intervals ?? [])
				.filter((interval) => !!interval.endDateTimeISO)
				.map((interval) => ({
					startDateTimeISO: interval.startDateTimeISO,
					endDateTimeISO: interval.endDateTimeISO!,
					schedules: interval.schedules,
				}))
		: [];
	const selectedOptionRawIntervals = hasSelectedIntervals
		? selectedIntervals.map((interval) => ({
				startDateTimeISO: interval.startDateTimeISO,
				endDateTimeISO: interval.endDateTimeISO!,
				schedules: interval.schedules,
			}))
		: selectedOptionDefaultIntervals;
	const selectedOptionCacheKey = getCapacityCacheKey(selectedOptionRawIntervals);
	const fullCapacityLabel = t`No Spots Left`;
	const fullSelectedTimeMessage = t`No spots left. Try a different time`;
	const clearTimeCapacityError = useCallback(() => {
		setTimeModalCapacityError(undefined);
	}, []);
	const shouldFetchSelectedCapacity = Boolean(
		selectedParkingOption && (!selectedOptionRequiresUserDateTimeInput || hasSelectedIntervals),
	);
	const { data: selectedOptionCapacity } = offstreetClient.locationLookup.getParkingChoiceCapacityStatus.useQuery(
		{
			parkingChoiceId: selectedParkingOption?.permitTypeId ?? 0,
			rawIntervals: selectedOptionRawIntervals,
		},
		{
			enabled: shouldFetchSelectedCapacity,
			refetchOnWindowFocus: false,
		},
	);
	const [capacityByOptionId, setCapacityByOptionId] = useState<Record<number, ChoiceCapacityStatusSnapshot>>({});
	const normalizedSelectedOptionCapacity =
		selectedParkingOption && selectedOptionCapacity ? selectedOptionCapacity : null;
	const effectiveCapacityByOptionId = !normalizedSelectedOptionCapacity
		? capacityByOptionId
		: {
				...capacityByOptionId,
				[normalizedSelectedOptionCapacity.parkingChoiceId]: {
					cacheKey: selectedOptionCacheKey,
					choiceCapacity: normalizedSelectedOptionCapacity.choiceCapacity,
					intervalSource: normalizedSelectedOptionCapacity.intervalSource,
					locations: normalizedSelectedOptionCapacity.locations,
				},
			};
	const getOptionTitle = (parkingOption: WizardParkingChoice) =>
		(parkingOption.title ?? "").length > 0 ? parkingOption.title : t`Date & Time`;
	const handleFullCapacity = useCallback(
		(parkingOption: WizardParkingChoice) => {
			const choiceIndex = formChoices.findIndex(
				(choice) => choice.parkingChoiceId === parkingOption.permitTypeId,
			);
			if (requiresUserDateTimeInput(parkingOption)) {
				setTimeModalCapacityError(fullSelectedTimeMessage);
				setValue("selectedChoicesIds", [parkingOption.permitTypeId], {
					shouldValidate: false,
				});
				if (choiceIndex >= 0) {
					setValue(`choices.${choiceIndex}.locationIds`, [], {
						shouldValidate: false,
					});
				}
				clearErrors("selectedChoicesIds");
				setIsLocationModalOpen(false);
				setIsTimeModalOpen(true);
				return;
			}
			setValue("selectedChoicesIds", [], {
				shouldValidate: false,
			});
			clearErrors("selectedChoicesIds");
			if (choiceIndex >= 0) {
				setValue(`choices.${choiceIndex}.locationIds`, [], {
					shouldValidate: false,
				});
			}
			setIsLocationModalOpen(false);
			setIsTimeModalOpen(false);
		},
		[clearErrors, formChoices, fullSelectedTimeMessage, setValue],
	);
	const openOptionTimeSelection = useCallback(
		(optionId: number) => {
			const parkingOption = parkingOptions.find((option) => option.permitTypeId === optionId);
			const optionCapacity = effectiveCapacityByOptionId[optionId]?.choiceCapacity ?? parkingOption?.capacity;
			const shouldShowSelectedTimeFullMessage =
				optionCapacity === "full" && parkingOption && requiresUserDateTimeInput(parkingOption);
			setTimeModalCapacityError(shouldShowSelectedTimeFullMessage ? fullSelectedTimeMessage : undefined);
			setValue("selectedChoicesIds", [optionId], {
				shouldValidate: false,
			});
			clearErrors("selectedChoicesIds");
			setIsLocationModalOpen(false);
			setIsTimeModalOpen(true);
		},
		[clearErrors, effectiveCapacityByOptionId, fullSelectedTimeMessage, parkingOptions, setValue],
	);
	const getRawIntervalsForOption = useCallback(
		(parkingOption: WizardParkingChoice) => {
			const formChoice = formChoices.find((choice) => choice.parkingChoiceId === parkingOption.permitTypeId);
			const formIntervals = mapPermitIntervals(formChoice?.permitIntervals ?? [], timezone);
			const hasFormIntervals =
				formIntervals.length > 0 && formIntervals.every((interval) => !!interval.endDateTimeISO);
			if (hasFormIntervals) {
				return formIntervals.map((interval) => ({
					startDateTimeISO: interval.startDateTimeISO,
					endDateTimeISO: interval.endDateTimeISO!,
					schedules: interval.schedules,
				}));
			}
			return (parkingOption.time?.intervals ?? [])
				.filter((interval) => !!interval.endDateTimeISO)
				.map((interval) => ({
					startDateTimeISO: interval.startDateTimeISO,
					endDateTimeISO: interval.endDateTimeISO!,
					schedules: interval.schedules,
				}));
		},
		[formChoices, timezone],
	);
	const shouldValidateCapacityBeforeSelect = useCallback(
		(parkingOption: WizardParkingChoice) => {
			const requiresDateTimeInput = requiresUserDateTimeInput(parkingOption);
			if (!requiresDateTimeInput) {
				return true;
			}
			const rawIntervals = getRawIntervalsForOption(parkingOption);
			return rawIntervals.length > 0;
		},
		[getRawIntervalsForOption],
	);
	const displayParkingOptions = parkingOptions.map((parkingOption) => {
		const cachedCapacity = effectiveCapacityByOptionId[parkingOption.permitTypeId];
		const currentCacheKey = getCapacityCacheKey(getRawIntervalsForOption(parkingOption));
		const isUnselectedEditableOption =
			selectedOptionId !== parkingOption.permitTypeId && requiresUserDateTimeInput(parkingOption);
		const shouldUseStaleCacheForDisplay =
			isUnselectedEditableOption && !!cachedCapacity && cachedCapacity.cacheKey !== currentCacheKey;
		if (!cachedCapacity || (cachedCapacity.cacheKey !== currentCacheKey && !shouldUseStaleCacheForDisplay)) {
			return parkingOption;
		}
		const locationCapacityById = new Map(
			cachedCapacity.locations.map((location) => [location.id, location.capacity]),
		);
		return {
			...parkingOption,
			capacity: cachedCapacity.choiceCapacity,
			locations: parkingOption.locations.map((location) => ({
				...location,
				capacity: locationCapacityById.get(location.id) ?? location.capacity,
			})),
		};
	});
	const displaySelectedParkingOption = displayParkingOptions.find(
		(option) => option.permitTypeId === selectedOptionId,
	);
	const validateOptionCapacity = useCallback(
		async (parkingOption: WizardParkingChoice, rawIntervalsOverride?: ParkingChoiceRawInterval[]) => {
			const validationStartedAt = Date.now();
			const rawIntervals = rawIntervalsOverride ?? getRawIntervalsForOption(parkingOption);
			let response: GetParkingChoiceCapacityStatusOutput;
			try {
				response = await trpcUtils.locationLookup.getParkingChoiceCapacityStatus.fetch({
					parkingChoiceId: parkingOption.permitTypeId,
					rawIntervals,
				});
			} catch {
				response = toNotFullCapacityFallback(parkingOption);
			}
			const normalizedResponse = response;
			const elapsedMs = Date.now() - validationStartedAt;
			if (normalizedResponse.choiceCapacity === "full" && elapsedMs < MIN_FULL_CAPACITY_VALIDATION_MS) {
				setDelayedFullOptionId(parkingOption.permitTypeId);
				await new Promise((resolve) => setTimeout(resolve, MIN_FULL_CAPACITY_VALIDATION_MS - elapsedMs));
			}
			setCapacityByOptionId((prev) => ({
				...prev,
				[normalizedResponse.parkingChoiceId]: {
					cacheKey: getCapacityCacheKey(rawIntervals),
					choiceCapacity: normalizedResponse.choiceCapacity,
					intervalSource: normalizedResponse.intervalSource,
					locations: normalizedResponse.locations,
				},
			}));
			return normalizedResponse;
		},
		[getRawIntervalsForOption, trpcUtils.locationLookup.getParkingChoiceCapacityStatus],
	);
	const requestLocationModalOpen = useCallback(
		async (optionId: number, rawIntervalsOverride?: ParkingChoiceRawInterval[]) => {
			const parkingOption = displayParkingOptions.find((option) => option.permitTypeId === optionId);
			if (!parkingOption || validatingCapacityOptionId !== null || delayedFullOptionId !== null) {
				return;
			}
			const rawIntervals = rawIntervalsOverride ?? getRawIntervalsForOption(parkingOption);
			const shouldValidateCapacity = !requiresUserDateTimeInput(parkingOption) || rawIntervals.length > 0;
			if (!shouldValidateCapacity) {
				setIsLocationModalOpen(true);
				return;
			}
			setValidatingCapacityOptionId(optionId);
			try {
				const response = await validateOptionCapacity(parkingOption, rawIntervals);
				if (response.choiceCapacity !== "full") {
					setIsLocationModalOpen(true);
				} else {
					handleFullCapacity(parkingOption);
				}
			} finally {
				setDelayedFullOptionId(null);
				setValidatingCapacityOptionId(null);
			}
		},
		[
			delayedFullOptionId,
			displayParkingOptions,
			getRawIntervalsForOption,
			handleFullCapacity,
			validateOptionCapacity,
			validatingCapacityOptionId,
		],
	);
	const handleOptionChange = useCallback(
		async (optionId: number) => {
			const parkingOption = displayParkingOptions.find((option) => option.permitTypeId === optionId);
			if (
				!parkingOption ||
				parkingOption.override?.state === "disabled" ||
				parkingOption.availability === "outside-recurring-window" ||
				validatingCapacityOptionId !== null ||
				delayedFullOptionId !== null
			) {
				return;
			}
			if (!shouldValidateCapacityBeforeSelect(parkingOption)) {
				onSelectOption(optionId);
				return;
			}
			setValidatingCapacityOptionId(optionId);
			try {
				const response = await validateOptionCapacity(parkingOption);
				if (response.choiceCapacity === "full") {
					handleFullCapacity(parkingOption);
					return;
				}
				onSelectOption(optionId);
			} finally {
				setDelayedFullOptionId(null);
				setValidatingCapacityOptionId(null);
			}
		},
		[
			delayedFullOptionId,
			displayParkingOptions,
			handleFullCapacity,
			onSelectOption,
			shouldValidateCapacityBeforeSelect,
			validateOptionCapacity,
			validatingCapacityOptionId,
		],
	);
	const singleDisplayOption = isSingleOption ? displayParkingOptions[0] : undefined;
	const singleOptionHasUserSelectableTimeInputs = singleDisplayOption
		? requiresUserDateTimeInput(singleDisplayOption)
		: false;
	const singleOptionIsFullAndNotEditable =
		!!singleDisplayOption && singleDisplayOption.capacity === "full" && !singleOptionHasUserSelectableTimeInputs;
	const singleOptionIsOutsideRecurringWindow = singleDisplayOption?.availability === "outside-recurring-window";
	const singleOptionIsOverrideDisabled = singleDisplayOption?.override?.state === "disabled";
	return (
		<>
			<OverrideNoticeAlert notices={overrideNotices} onSelectPermitType={onSelectOverridePermitType} />
			{singleDisplayOption ? (
				<section
					className="space-y-2"
					aria-label={(() => {
						let label = getOptionTitle(singleDisplayOption) ?? "";
						if (singleOptionIsOutsideRecurringWindow && singleDisplayOption.recurringWindowStartsAt) {
							const dayStr = formatDate({
								date: singleDisplayOption.recurringWindowStartsAt,
								formatType: "Weekday",
								timezone,
								language: locale,
							});
							const timeStr = formatDate({
								date: singleDisplayOption.recurringWindowStartsAt,
								formatType: "Time",
								timezone,
								language: locale,
							});
							label += `, ${t`Registration starts ${dayStr} at ${timeStr}`}`;
						} else if (singleDisplayOption.rangeEnd) {
							const closesDate = formatDate({
								date: singleDisplayOption.rangeEnd,
								formatType: "Abbreviated_Date",
								timezone,
								language: locale,
							});
							const closesTime = formatDate({
								date: singleDisplayOption.rangeEnd,
								formatType: "Time",
								timezone,
								language: locale,
							});
							label += `, ${t`Option available until ${closesDate} at ${closesTime}`}`;
						}
						const formChoice = formChoices.find(
							(c) => c.parkingChoiceId === singleDisplayOption.permitTypeId,
						);
						const locations = singleDisplayOption.locationChoiceEnabled
							? singleDisplayOption.locations.filter((l) => formChoice?.locationIds?.includes(l.id))
							: singleDisplayOption.locations;
						if (locations.length > 0) {
							label += `, ${locations.map((l) => l.name).join(", ")}`;
						}
						return label;
					})()}
				>
					<div className="flex items-center justify-between gap-4">
						<ParkingChoiceTag choice={singleDisplayOption} timezone={timezone} />
						{singleOptionIsOverrideDisabled ? (
							<Badge variant="warning">
								{t({
									comment:
										"Badge on a parking option that cannot be selected because an active permit type override blocks it",
									message: "Unavailable",
								})}
							</Badge>
						) : (
							!singleOptionIsOutsideRecurringWindow &&
							singleDisplayOption.capacity === "full" && <CapacityFullBadge />
						)}
					</div>
					<ParkingOptionDetails
						parkingOption={singleDisplayOption}
						timezone={timezone}
						preSelectedLocationId={preSelectedLocationId}
						onOpenTimeModal={() => {
							if (!singleOptionIsOverrideDisabled) {
								setIsTimeModalOpen(true);
							}
						}}
						onOpenLocationModal={() => {
							if (!singleOptionIsOutsideRecurringWindow && !singleOptionIsOverrideDisabled) {
								void requestLocationModalOpen(singleDisplayOption.permitTypeId);
							}
						}}
						isSelectionPromptHidden={
							singleOptionIsFullAndNotEditable ||
							singleOptionIsOutsideRecurringWindow ||
							singleOptionIsOverrideDisabled
						}
						isValidatingCapacity={validatingCapacityOptionId === singleDisplayOption.permitTypeId}
					/>
				</section>
			) : (
				<RadioCard
					value={selectedOptionId?.toString() ?? ""}
					name="selectedChoicesIds"
					onChange={(val) => {
						void handleOptionChange(Number(val));
					}}
					aria-label={listAriaLabel}
					aria-labelledby={listAriaLabelledBy}
					options={displayParkingOptions.map((parkingOption) => {
						const isUpcoming = parkingOption.rangeStart && new Date(parkingOption.rangeStart) > new Date();
						const isOutsideRecurringWindow = parkingOption.availability === "outside-recurring-window";
						const hasUserSelectableTimeInputs = requiresUserDateTimeInput(parkingOption);
						const isOverrideDisabled = parkingOption.override?.state === "disabled";
						const disabled = isChoiceIneligible(parkingOption) || isOverrideDisabled;
						const isShowingDelayedFullIndicator = delayedFullOptionId === parkingOption.permitTypeId;
						const title = getOptionTitle(parkingOption);
						const hasLocationChoice = parkingOption.locationChoiceEnabled;
						const formChoiceIndex = formChoices.findIndex(
							(choice) => choice.parkingChoiceId === parkingOption.permitTypeId,
						);
						const formChoice = formChoiceIndex < 0 ? undefined : formChoices[formChoiceIndex];
						const locations = hasLocationChoice
							? parkingOption.locations.filter((l) => formChoice?.locationIds?.includes(l.id))
							: parkingOption.locations;
						const neverExpires = hasNeverExpiresIntervals(parkingOption.time);
						const hasTimesSelected =
							(formChoice?.permitIntervals?.length ?? 0) > 0 &&
							formChoice?.permitIntervals?.every(
								(x) => x.startDate && x.startTime && (neverExpires || (x.endDate && x.endTime)),
							);
						const intervalsForAriaLabel: AriaPermitInterval[] = hasTimesSelected
							? mapPermitIntervals(formChoice?.permitIntervals ?? [], timezone)
							: (parkingOption.time.intervals ?? []);
						const intervalAriaLabel = intervalsForAriaLabel
							.map((interval: AriaPermitInterval) => {
								if (!interval.endDateTimeISO) {
									const startDateTime = formatDate({
										date: interval.startDateTimeISO,
										formatType: "Abbreviated_DateTime",
										timezone,
										language: locale,
									});
									return t`${startDateTime} with no expiry`;
								}
								const dateRange = formatDateRange({
									startDate: interval.startDateTimeISO,
									endDate: interval.endDateTimeISO,
									formatType: "Abbreviated_DateTime",
									timezone,
									language: locale,
								});
								return t`${dateRange}`;
							})
							.join(t`; `);
						const ariaLabel = (() => {
							let label = title;
							if (parkingOption.capacity === "full") {
								label += `, ${fullCapacityLabel}`;
							}
							if (isOutsideRecurringWindow && parkingOption.recurringWindowStartsAt) {
								const dayStr = formatDate({
									date: parkingOption.recurringWindowStartsAt,
									formatType: "Weekday",
									timezone,
									language: locale,
								});
								const timeStr = formatDate({
									date: parkingOption.recurringWindowStartsAt,
									formatType: "Time",
									timezone,
									language: locale,
								});
								label += `, ${t`Registration starts ${dayStr} at ${timeStr}`}`;
							}
							if (intervalAriaLabel.length > 0) {
								label += `, ${intervalAriaLabel}`;
							}
							if (!isOutsideRecurringWindow && parkingOption.rangeStart && parkingOption.rangeEnd) {
								const intervals = parkingOption.time.intervals ?? [];
								const lastInterval = intervals[intervals.length - 1];
								if (
									isUpcoming ||
									(lastInterval && lastInterval.endDateTimeISO !== parkingOption.rangeEnd)
								) {
									const date = isUpcoming ? parkingOption.rangeStart : parkingOption.rangeEnd;
									const dateStr = formatDate({
										date,
										formatType: "Abbreviated_Date",
										timezone,
										language: locale,
									});
									const timeStr = formatDate({
										date,
										formatType: "Time",
										timezone,
										language: locale,
									});
									const tagMessage = isUpcoming
										? t`Option available ${dateStr} at ${timeStr}`
										: t`Option available until ${dateStr} at ${timeStr}`;
									label += `, ${tagMessage}`;
								}
							}
							if (locations.length > 0) {
								const locationNames = locations.map((l) => l.name).join(", ");
								label += `, ${locationNames}`;
							}
							return label;
						})();
						const choiceErrors =
							formChoiceIndex < 0 ? undefined : formState.errors.choices?.[formChoiceIndex];
						const locationError = choiceErrors?.locationIds?.message;
						const timeError = choiceErrors?.permitIntervals?.message;
						return {
							label: (
								<span>
									<span className="flex w-full items-center justify-between gap-4">
										{title}

										{isShowingDelayedFullIndicator ? (
											<span className="inline-flex items-center rounded-sm border border-neutral-faded bg-neutral-faded px-2 py-0.5">
												<LoadingSpinner className="text-neutral-faded" size="sm" />
											</span>
										) : isOverrideDisabled ? (
											<Badge variant="warning">
												{t({
													comment:
														"Badge on a parking option that cannot be selected because an active permit type override blocks it",
													message: "Unavailable",
												})}
											</Badge>
										) : (
											!isOutsideRecurringWindow &&
											parkingOption.capacity === "full" && <CapacityFullBadge />
										)}
									</span>
									<ParkingChoiceTag choice={parkingOption} timezone={timezone} />
								</span>
							),
							"aria-label": ariaLabel,
							value: parkingOption.permitTypeId.toString(),
							disabled:
								disabled ||
								validatingCapacityOptionId === parkingOption.permitTypeId ||
								isShowingDelayedFullIndicator,
							onClick: () => {
								if (disabled) {
									return;
								}
								const hasLocationsSelected = locations.length > 0;
								const canAutoSelectLocation =
									hasLocationChoice &&
									preSelectedLocationId &&
									parkingOption.locations.some((l) => l.id === preSelectedLocationId);
								const shouldOpenTimeModalForFullEditableOption =
									parkingOption.capacity === "full" && hasUserSelectableTimeInputs;
								if (shouldOpenTimeModalForFullEditableOption) {
									openOptionTimeSelection(parkingOption.permitTypeId);
								} else if (hasUserSelectableTimeInputs && !hasTimesSelected) {
									openOptionTimeSelection(parkingOption.permitTypeId);
								} else if (hasLocationChoice && !hasLocationsSelected && !canAutoSelectLocation) {
									void requestLocationModalOpen(parkingOption.permitTypeId);
								}
							},
							"aria-invalid": !!locationError || !!timeError,
							children: (
								<div className={disabled ? "opacity-50" : ""}>
									<ParkingOptionDetails
										parkingOption={parkingOption}
										timezone={timezone}
										preSelectedLocationId={preSelectedLocationId}
										onOpenTimeModal={() => {
											if (!disabled) {
												setIsTimeModalOpen(true);
											}
										}}
										onOpenLocationModal={() => {
											if (!disabled) {
												void requestLocationModalOpen(parkingOption.permitTypeId);
											}
										}}
										isSelectionPromptHidden={
											disabled ||
											(parkingOption.capacity === "full" && !hasUserSelectableTimeInputs)
										}
										isValidatingCapacity={validatingCapacityOptionId === parkingOption.permitTypeId}
										isSelectableOption
									/>
								</div>
							),
						};
					})}
				/>
			)}
			{displaySelectedParkingOption && (
				<>
					<TimeSelectionForm
						key={selectedOptionId}
						isOpen={isTimeModalOpen}
						onOpenChange={(open) => {
							if (!open) {
								setIsTimeModalOpen(false);
								clearTimeCapacityError();
							}
						}}
						title={t`Choose Parking Time`}
						optionName={getOptionTitle(displaySelectedParkingOption) ?? ""}
						permitIntervals={selectedFormChoice?.permitIntervals ?? []}
						wizardSettings={displaySelectedParkingOption.time}
						timezone={timezone}
						onSubmit={async (intervals) => {
							onSelectTime(intervals);
							clearTimeCapacityError();
							const submittedRawIntervals = toRawIntervals(mapPermitIntervals(intervals, timezone));
							const capacityResponse = await validateOptionCapacity(
								displaySelectedParkingOption,
								submittedRawIntervals,
							);
							setDelayedFullOptionId(null);
							if (capacityResponse.choiceCapacity === "full") {
								setTimeModalCapacityError(fullSelectedTimeMessage);
								return;
							}
							setIsTimeModalOpen(false);
							const hasLocationsSelected = (selectedFormChoice?.locationIds?.length ?? 0) > 0;
							const canAutoSelectLocation =
								preSelectedLocationId &&
								displaySelectedParkingOption.locations.some((l) => l.id === preSelectedLocationId);
							const shouldOpenLocationModal =
								displaySelectedParkingOption.locationChoiceEnabled &&
								!hasLocationsSelected &&
								!canAutoSelectLocation;
							if (shouldOpenLocationModal) {
								await requestLocationModalOpen(
									displaySelectedParkingOption.permitTypeId,
									submittedRawIntervals,
								);
								return;
							}
						}}
						capacityErrorMessage={timeModalCapacityError}
					/>
					<LocationSelectionOverlay
						isOpen={isLocationModalOpen}
						onOpenChange={(open) => {
							if (!open) {
								setIsLocationModalOpen(false);
							}
						}}
						title={t`Select a location`}
						optionName={getOptionTitle(displaySelectedParkingOption) ?? ""}
						timezone={timezone}
						permitIntervals={selectedIntervals}
						locations={displaySelectedParkingOption.locations}
						onLocationClick={(locationId) => {
							onSelectOption(displaySelectedParkingOption.permitTypeId, locationId);
							setIsLocationModalOpen(false);
						}}
						isInstant={
							!!displaySelectedParkingOption.time?.startDate?.instant &&
							!!displaySelectedParkingOption.time?.startTime?.instant
						}
						context={displaySelectedParkingOption.context}
					/>
				</>
			)}
		</>
	);
};
